import { listenerMiddleware } from './listener.middleware';
import { isAnyOf } from '@reduxjs/toolkit';
import {
  GroupedMedicalDocuments,
  medicalDocumentApi,
  MedicalDocumentType
} from '@/modules/surgicalEvent/medicalDocument';
import { SetTimeout } from '@/util';
import { secondsToMilliseconds } from 'date-fns';
import { ID } from '@ianesthesie-kit/services';

let lastPatientId: ID;
let invalidationTimeout: SetTimeout;
const currentlyPendingExtractionDocumentIds = new Set<ID>();

listenerMiddleware.startListening({
  matcher: isAnyOf(
    medicalDocumentApi.endpoints.medicalDocuments.matchFulfilled,
    medicalDocumentApi.endpoints.medicalDocumentsByType.matchFulfilled
  ),
  effect: async (action, api) => {
    const { patientId } = (action.meta as any).arg.originalArgs;
    const documents = Object.values(
      (action.payload ?? {}) as GroupedMedicalDocuments
    ).flat();
    if (invalidationTimeout) {
      clearTimeout(invalidationTimeout);
    }
    if (!documents.length) {
      currentlyPendingExtractionDocumentIds.clear();
      return;
    }

    // if another patient is displayed, clear pending
    if (lastPatientId !== patientId) {
      currentlyPendingExtractionDocumentIds.clear();
      lastPatientId = patientId;
    }

    // first check if we have any extraction that was previously in progress
    if (currentlyPendingExtractionDocumentIds.size) {
      //  check if any of them is now settled and if any, invalidate relevant tags
      documents
        .filter(
          (doc) =>
            !!doc.extractionStatus &&
            doc.extractionStatus !== 'PENDING' &&
            currentlyPendingExtractionDocumentIds.has(doc.id)
        )
        .forEach((doc) => {
          currentlyPendingExtractionDocumentIds.delete(doc.id);
          if (doc.extractionStatus === 'COMPLETED') {
            if (doc.type === MedicalDocumentType.PRESCRIPTION) {
              api.dispatch(
                medicalDocumentApi.util.invalidateTags([
                  { type: 'Meds', id: patientId },
                  { type: 'SurgeryInfo', id: patientId }, // for ASA prediction
                  { type: 'MedicalHo', id: patientId }, // for meds automation relevant mho items
                  'Dashboard' // for medsCount
                ])
              );
            } else if (doc.type === MedicalDocumentType.BLOODWORK) {
              api.dispatch(
                medicalDocumentApi.util.invalidateTags([
                  { type: 'LabTest', id: patientId },
                  { type: 'Protocol', id: patientId },
                  { type: 'ProtocolStatuses', id: patientId },
                  { type: 'SurgeryInfo', id: patientId }, // for ASA prediction
                  { type: 'MedicalHo', id: patientId }, // for meds automation relevant mho items
                  'Dashboard' // for medsCount
                ])
              );
            } else if (doc.type === MedicalDocumentType.CARDIAC_TEST) {
              api.dispatch(
                medicalDocumentApi.util.invalidateTags([
                  { type: 'LabTest', id: patientId }
                ])
              );
            } else if (doc.type === MedicalDocumentType.OPERATOR_INVOICE) {
              api.dispatch(
                medicalDocumentApi.util.invalidateTags([
                  { type: 'SurgeryInfo', id: patientId }
                ])
              );
            }
          }
        });
    }

    // store all pending extraction document ids
    documents
      .filter((doc) => doc.extractionStatus === 'PENDING')
      .forEach((doc) => currentlyPendingExtractionDocumentIds.add(doc.id));

    // if we have any extraction pending, we need to invalidate medical documents tag
    if (currentlyPendingExtractionDocumentIds.size) {
      invalidationTimeout = setTimeout(() => {
        api.dispatch(
          medicalDocumentApi.util.invalidateTags([
            { type: 'MedicalDocument', id: `${patientId}_medical` },
            { type: 'MedicalDocument', id: `${patientId}_byType` }
          ])
        );
      }, secondsToMilliseconds(7));
    }
  }
});
